import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
	CustomSpinner,
	ToggleButton,
	CustomTable,
	DistrictMap,
	SaveCustomSearch,
} from '@components';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '@store';
import {
	addSaveSearch,
	districtPropertyCustomSearch,
	getListSavedSearch,
	removeSaveSearch,
} from '@services';
import {
	categoryValueSelector,
	debounce,
	generateFilterString,
	genrateHousingString,
	toastSuccessHelper,
	transformCustomSearchData,
} from '@utils';
import { clearCustomSearchOptionState } from '@/store/slice';
import { CustomSearch } from '@/pages';
import { geographyOptions } from '@mockData';
import { CiStar } from 'react-icons/ci';
import { GiRoundStar } from 'react-icons/gi';

enum ViewMode {
	MAP = 'MAP',
	TABLE = 'TABLE',
}

export const DistrictSeachResult: React.FC = () => {
	const { module, id } = useParams<{
		module: string;
		id: string;
	}>();
	const location = useLocation();
	const searchParams = new URLSearchParams(location.search);
	const queryParams = Object.fromEntries(searchParams.entries());
	const dispatch = useDispatch<AppDispatch>();
	const navigate = useNavigate();
	const [error, setError] = useState<string | null>(null);
	const [selectedGeography, setSelectedGeography] = useState<string>('');
	const [geographyValue, setGeographyValue] = useState<string>('');
	const [housingString, setHousingString] = useState<string>('');
	const [filterString, setFilterSting] = useState<string>('');
	const [isSearchSaved, setIsSearchSaved] = useState<boolean>(false);
	const [isModelOpen, setIsModelOpen] = useState<boolean>(false);
	const modalRef = useRef<HTMLDivElement | null>(null);

	// State Variables
	const [districtData, setDistrictData] = useState<any[]>([]);
	const [propertyCount, setPropertyCount] = useState(0);
	const [unitCount, setUnitCount] = useState(0);
	const [isResultView, setIsResultView] = useState<boolean>(true);
	const [view, setView] = useState(ViewMode.MAP);
	const [lodder, setLodder] = useState<boolean>(false);
	const [booMarkLodder, setBookMarkLodder] = useState<boolean>(false);
	const { isLoggedIn } = useSelector(
		(Root: RootState) => Root.rootSlice.application
	);
	const {
		SelectedHousingType,
		Addfilters,
		Housingfilters,
		LogicalOperator,
		navigateParam,
	} = useSelector(
		(state: RootState) => state.rootSlice.application.customSearchOptionState
	);
	const handleViewChange = useCallback(() => {
		setView((prevView) =>
			prevView === ViewMode.MAP ? ViewMode.TABLE : ViewMode.MAP
		);
	}, []);

	const closeModal = useCallback(() => setIsModelOpen(false), []);

	useEffect(() => {
		setIsResultView(true);
		getGeographyLabelbyValue();
		getAPIResponse();
	}, [
		module,
		id,
		location.search,
		Addfilters,
		Housingfilters,
		LogicalOperator,
	]); // Added location.search dependency to reload when query params change

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (
				modalRef.current &&
				!modalRef.current.contains(event.target as Node)
			) {
				closeModal();
			}
		};

		if (isModelOpen) {
			document.addEventListener('mousedown', handleClickOutside);
		}
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [isModelOpen]);

	useEffect(() => {
		if (isLoggedIn) checkCustomSearchMarked();
		else {
			setIsSearchSaved(false);
		}
	}, [isLoggedIn]);

	const checkCustomSearchMarked = useCallback(async () => {
		try {
			setIsSearchSaved(false);
			const response = await getListSavedSearch(navigateParam);
			if (response.status === 200 && response?.data?.data?.length > 0) {
				setIsSearchSaved(true);
			}
		} catch (e) {
			console.log(e);
		}
	}, []);

	const getGeographyLabelbyValue = async () => {
		if (module) {
			const selectedGeo =
				geographyOptions.find((item) => item.value === module)?.label || '';
			setSelectedGeography(selectedGeo);
			const options = categoryValueSelector(module);
			const geoValue = options.find((item) => item.value === id)?.label || '';
			setGeographyValue(geoValue);
		}
		const housingDisplayString = await genrateHousingString(Housingfilters);
		setHousingString(housingDisplayString);
		const filterDisplayString = generateFilterString(
			Addfilters,
			LogicalOperator
		);
		setFilterSting(filterDisplayString);
	};

	const getAPIResponse = async () => {
		setError(null);
		if (module && id && queryParams) {
			try {
				setLodder(true);
				const response = await districtPropertyCustomSearch({
					module,
					id,
					params: queryParams,
				});
				const transformData = transformCustomSearchData(response?.data);
				const units = transformData.reduce(
					(sum, b) => sum + b['Residential Units'],
					0
				);
				setUnitCount(units);
				setPropertyCount(transformData.length);
				setDistrictData(transformData);
				setLodder(false);
			} catch (error: any) {
				setLodder(false);
				console.error('Error fetching custom search results:', error);
				setError(error?.message || 'Something went wrong');
			}
		}
	};

	// Handle custom search submission from nested component
	const handleCustomSearchSubmit = (url: string) => {
		setError(null);
		// Extract query params and trigger API call without navigation
		const urlObj = new URL(window.location.origin + url);
		const newParams = Object.fromEntries(urlObj.searchParams.entries());

		// Update URL without full page reload using history API
		window.history.pushState({}, '', url);

		// Update state to reflect new search
		setIsResultView(true);

		// Manually trigger API call with new parameters
		if (module && id && newParams) {
			setLodder(true);
			districtPropertyCustomSearch({
				module,
				id,
				params: newParams,
			})
				.then((response) => {
					const transformData = transformCustomSearchData(response?.data);
					const units = transformData.reduce(
						(sum, b) => sum + b['Residential Units'],
						0
					);
					setUnitCount(units);
					setPropertyCount(transformData.length);
					setDistrictData(transformData);
					setLodder(false);
				})
				.catch((error) => {
					setLodder(false);
					setError(error?.message || 'Something went wrong');
					console.error('Error fetching custom search results:', error);
				});
		}
	};

	const saveCustomSeachBookmakred = useCallback(
		async (name: string) => {
			setBookMarkLodder(true);
			try {
				const response = await addSaveSearch({
					name,
					addFilters: Addfilters,
					housingType: SelectedHousingType,
					housingFilters: Housingfilters,
					logicalOperator: LogicalOperator,
					navigateParam,
				});
				if (response.status === 200) {
					setIsSearchSaved(true);
					toastSuccessHelper('Successfully bookmarked this property!');
				}
			} catch (e: any) {
				e?.response?.data?.message || 'Something went wrong';
			}
			setBookMarkLodder(false);
		},
		[Addfilters, Housingfilters, navigateParam, LogicalOperator]
	);

	const debouncedFetchResults = useCallback(
		debounce(saveCustomSeachBookmakred, 300),
		[saveCustomSeachBookmakred]
	);

	const handleCustomSearchBookmarkd = async () => {
		console.log('Saving custom search bookmark	');
		if (isSearchSaved) {
			const result = window.confirm(
				'Are you sure you want to delete this saved search?'
			);
			if (result) {
				setBookMarkLodder(true);
				try {
					console.log(navigateParam);
					const response = await removeSaveSearch(navigateParam);
					if (response.status === 200) {
						setIsSearchSaved(false);
						toastSuccessHelper('Successfully deleted this bookmark!');
					}
				} catch (e: any) {
					e?.response?.data?.message || 'Something went wrong';
				}
				setBookMarkLodder(false);
			}
		} else {
			setIsModelOpen(true);
		}
	};

	const onSubmit = (name: string) => {
		setIsModelOpen(false);
		debouncedFetchResults(name);
	};

	return (
		<div className="flex flex-col w-full mt-10">
			<div className="transition-all duration-300 w-full px-4 md:px-10 mb-10">
				{!lodder && (
					<div className="border border-gray-700 p-4 w-full mb-4">
						<div className="flex items-center justify-between mb-2">
							<div className="flex items-center gap-1 text-sm">
								<span>{`Displaying ${selectedGeography}${housingString && `(${housingString})`} properties in ${geographyValue} ${filterString}`}</span>
							</div>
							{isLoggedIn && (
								<div
									className="inline-flex items-center min-w-fit cursor-pointer"
									onClick={handleCustomSearchBookmarkd}
								>
									{booMarkLodder && (
										<div>
											<CustomSpinner width={17} height={17} />
										</div>
									)}
									{isSearchSaved ? (
										<>
											<GiRoundStar className="h-6 w-6" />
											You have saved this search
										</>
									) : (
										<>
											<CiStar className="h-7 w-7" />
											Save the search
										</>
									)}
								</div>
							)}
						</div>
						<div className="flex flex-wrap items-center justify-between">
							<div className="text-sm font-bold inline-flex flex-wrap leading-tight gap-4">
								<div>
									<span>
										Properties: <b>{propertyCount}</b>
									</span>
								</div>
								<div>
									<span>
										Units: <b>{unitCount}</b>
									</span>
								</div>
							</div>
							<div className="space-x-3">
								<button
									className="btn-light py-1 px-2 border border-gray-600 rounded-sm"
									onClick={() => {
										dispatch(clearCustomSearchOptionState());
										navigate('/search');
									}}
								>
									Clear
								</button>

								<button
									onClick={() => setIsResultView((prev) => !prev)}
									className="btn-dark py-1 px-2 border-gray-600 rounded-sm"
								>
									{isResultView ? `Edit Custom Search` : 'View Results'}
								</button>
							</div>
						</div>
					</div>
				)}
				{error && (
					<div className="text-center rounded-sm border border-red-700 p-4 mb-4">
						<h1 className="text-red-700 font-semibold">{error}</h1>
					</div>
				)}

				{!isResultView ? (
					<CustomSearch
						module={module}
						id={id}
						navigateFunction={navigate}
						onSearchSubmit={handleCustomSearchSubmit}
						isNested={true}
					/>
				) : (
					<>
						{lodder ? (
							<div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30">
								<CustomSpinner width={100} height={100} border={10} />
							</div>
						) : (
							<>
								<div className="flex justify-end mt-3 md:hidden">
									<ToggleButton
										label="MAP"
										isActive={view === ViewMode.MAP}
										onClick={handleViewChange}
									/>
									<ToggleButton
										label="TABLE"
										isActive={view === ViewMode.TABLE}
										onClick={handleViewChange}
									/>
								</div>

								{/* Mobile View: Show either Map or Table based on toggle */}
								<div className="mt-4 md:hidden">
									{view === ViewMode.MAP ? (
										<div>
											<DistrictMap
												locations={districtData}
												currentModule={module}
											/>
										</div>
									) : (
										<CustomTable data={districtData} headerClass="mb-4" />
									)}
								</div>

								{/* Desktop/Medium Screen View: Show Map and Table side by side */}
								<div className="hidden md:flex mt-4 gap-4">
									<div className="w-1/3">
										<DistrictMap
											locations={districtData}
											currentModule={module}
											mapHeight={
												districtData.length > 0 ? 'h-[500px]' : 'h-[300px]'
											}
										/>
									</div>
									<div className="w-2/3">
										<CustomTable data={districtData} headerClass="mb-4" />
									</div>
								</div>
							</>
						)}
					</>
				)}
			</div>
			<div>
				{isModelOpen && (
					<SaveCustomSearch
						query={`${selectedGeography}${housingString && `(${housingString})`} properties in ${geographyValue} ${filterString}`}
						onClose={closeModal}
						onSubmit={onSubmit}
						modelref={modalRef}
					/>
				)}
			</div>
		</div>
	);
};
